/**
 * Data for the App Experience page: the UNCACHED admin view (live + history),
 * publish with the version the admin saw, and the names of the admins in the
 * history. Shared by every tab, so a publish on one tab refreshes them all.
 *
 * Concurrency (spec §5): publish sends `based_on_version`. When someone else
 * published in between, the server answers 409. The page then says so twice:
 * a toast, which is gone after 5 s, and a banner with a Reload button, which
 * stays until the admin reloads — a missed toast must not leave a dead end.
 *
 * Every other failure gets plain words that say what is wrong and how to fix
 * it (`publishOutcome` → `publishErrorText`, ruling R17) — never the raw server
 * text, a URL or a developer word. Both tabs publish through here, so both get
 * the same words.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';
import type { ExperienceSurface } from './lib/contract';
import { fetchAdminNames, fetchAdminView, type AdminAppExperienceView, type PublishBody } from './api';
import { CONFLICT_TEXT, publishOutcome, type PublishOutcome } from './publish-outcome';
import { loadErrorText } from './lib/error-words';

export { CONFLICT_TEXT, type PublishOutcome };

export function useAppExperience() {
  const apiUrl = useApiUrl();
  const { admin, hasPermission, loaded: permsLoaded } = useAdminPermissions();
  const [view, setView] = useState<AdminAppExperienceView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** Reads the view. Keeps the last good view when a reload fails, and says so. */
  const reload = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      const v = await fetchAdminView(apiUrl);
      if (!alive.current) return false;
      setView(v);
      setLoadError(null);
      setStale(false);
      return true;
    } catch (e) {
      // Plain words (R17): never a raw session, HTTP or "Failed to fetch" message.
      if (alive.current) setLoadError(loadErrorText(e));
      return false;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Names for the history. Only an admin who may list admins can read them;
  // anyone else sees "You" or a short id. A failure here is not worth a message.
  const canListAdmins = permsLoaded && hasPermission('admin.list');
  useEffect(() => {
    if (!canListAdmins) return;
    let cancelled = false;
    fetchAdminNames(apiUrl)
      .then((rows) => {
        if (cancelled) return;
        setNames(new Map(rows.map((r) => [r.id, r.display_name || r.email || r.id.slice(0, 8)])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [apiUrl, canListAdmins]);

  const whoName = useCallback(
    (id: string): string => {
      if (admin && id === admin.id) return 'You';
      return names.get(id) ?? `Admin ${id.slice(0, 8)}`;
    },
    [admin, names],
  );

  const publish = useCallback(
    async (surface: ExperienceSurface, body: PublishBody): Promise<PublishOutcome> => {
      const r = await publishOutcome(apiUrl, surface, body);
      if (r.ok) {
        await reload();
      } else if (r.conflict) {
        if (alive.current) setStale(true);
        toast({ title: CONFLICT_TEXT, variant: 'destructive' });
      }
      return r;
    },
    [apiUrl, reload],
  );

  return { apiUrl, view, loading, loadError, stale, reload, publish, whoName };
}

export type AppExperienceData = ReturnType<typeof useAppExperience>;

/**
 * Asks the browser to warn before the tab closes or reloads while `active`
 * (an upload in progress, or changes not yet published). Navigation inside
 * the panel is not covered: the router here has no blocking API.
 */
export function useLeaveWarning(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [active]);
}
